import { Injectable } from '@angular/core';
import { IAddress, IAddressItem } from '../models/address.interface';
import { HttpService } from './http.service';
import { EndpointsService } from './endpoints.service';
import { Firestore, addDoc, collection, deleteDoc, doc, getCountFromServer, getDoc, getDocs, updateDoc } from '@angular/fire/firestore';
import { Storage } from '@ionic/storage-angular';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class AddressesService {

  private _storage: Storage | null = null;

  private _currentAddressItemUid = '';
  private _currentAddress!: IAddress;

  get currentAddress() {
    return this._currentAddress;
  }

  get currentAddressItemUid() {
    return this._currentAddressItemUid;
  }

  set currentAddressItemUid(value: string) {
    this._currentAddressItemUid = value;
  }

  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private httpService: HttpService,
    private endpoints: EndpointsService,
    private storage: Storage
  ) {
    this.clearCurrentAddress();
    this.init();
  }

  async init() {
    const storage = await this.storage.create();
    this._storage = storage;
  }

  getGuestZipCode(): Promise<string> {
    return this.storage.get('guest_zip_code')
  }

  getGuestPosition(): Promise<string> {
    return this.storage.get('guest_position')
  }

  clearCurrentAddress() {
    this._currentAddress = {
      active: false,
      position: '',
      address1: '',
      exterior_number: '',
      interior_number: '',
      zip: '',
      state: '',
      county: '',
      neighborhood: '',
    }
    this._currentAddressItemUid = '';
  }

  getFirstAddressFromZipCode(zipCode: String): Promise<string | any> {
    return new Promise<string | any>((resolve) => {
      this.httpService.get(this.endpoints.address + '/' + zipCode).subscribe({
        next: (response: any) => {
          if (response.success) {
            if (response.data.length <= 0) {
              resolve('Error: No se encontraron datos del código postal.');
            } else {
              resolve(response.data[0]);
            }
          } else {
            resolve(`Error: ${response.message}`);
          }
        }, error: (err) => {
          if (err.error.message) {
            resolve(`Error: ${err.error.message}`);
          } else {
            resolve('Error: Ocurrió un error con el servicio de geolocalización.');
          }
        }
      })
    });
  }

  getAddressFromZipCode(zipCode: String) {
    return this.httpService.get(this.endpoints.address + '/' + zipCode);
  }

  async registerGuestZip(zipcode: string, position: string): Promise<boolean> {
    //this.storage.set('guest_zip_code', zipcode);
    //this.storage.set('guest_position', position);

    this.storage.set('guest_zip_code', 'trial');
    this.storage.set('guest_position', 'trial');

    this._currentAddress = {
      active: true,
      position: position,
      address1: '',
      exterior_number: '',
      interior_number: '',
      zip: zipcode,
      state: '',
      county: '',
      neighborhood: '',
    };

    return true;
  }

  async register(userRegistrationId: string, registration: any) {
    const firestore = this.firestore;

    this._currentAddress = {
      active: registration.active,
      position: registration.position,
      address1: registration.address1,
      exterior_number: registration.exterior_number,
      interior_number: registration.interior_number,
      zip: registration.zip,
      state: registration.state,
      county: registration.county,
      neighborhood: registration.neighborhood,
    }

    try {
      if (this._currentAddressItemUid) {
        await updateDoc(doc(firestore, `userProfile/${userRegistrationId}/addresses`, this._currentAddressItemUid), registration);
        // Set active item and ensure it is the only active item.
        if (this._currentAddress.active) {
          await this.setActiveItem(userRegistrationId, this._currentAddressItemUid);
        }
      } else {
        const docRef = await addDoc(collection(firestore, `userProfile/${userRegistrationId}/addresses`), this._currentAddress);
        // Set active item and ensure it is the only active item.
        if (registration.active) {
          await this.setActiveItem(userRegistrationId, docRef.id);
        }
      }

      return true;
    } catch (e) {
      console.log('Error saving address: EA01. ', e);
      return false;
    }
  }

  async list(userRegistrationId: string) {
    let addresses = new Array<IAddressItem>();
    if (await this.authService.getGuestFlag()) {
      const zipCode = await this.getGuestZipCode();
      const position = await this.getGuestPosition();
      const guestId = await this.authService.getGuestUuid();
      if (zipCode) {
        const address = {
          uid: `guest_add_${guestId}`,
          position: position,
          active: true,
          address1: '',
          address2: '',
          address3: '',
          address4: zipCode
        };
        addresses.push(address)
        this._currentAddress = {
          active: address.active,
          position: address.position,
          address1: '',
          exterior_number: '',
          interior_number: '',
          zip: address.address4,
          state: '',
          county: '',
          neighborhood: ''
        };
        this._currentAddressItemUid = address.uid;
      }
      return addresses;
    } else {
      try {
        const addressesDocs = await getDocs(collection(this.firestore, `userProfile/${userRegistrationId}/addresses`))

        addressesDocs.forEach(addressDoc => {
          if (addressDoc.exists()) {
            const address = {
              uid: addressDoc.id,
              active: addressDoc.get('active'),
              position: addressDoc.get('position'),
              address1: `${addressDoc.get('address1')} ${addressDoc.get('exterior_number')}-${addressDoc.get('interior_number')}`,
              address2: `${addressDoc.get('neighborhood')}`,
              address3: `${addressDoc.get('county')}, ${addressDoc.get('state')}`,
              address4: `${addressDoc.get('zip')}`,
            }
            addresses.push(address);
            if (address.active) {
              this._currentAddress = {
                active: addressDoc.get('active'),
                position: addressDoc.get('position'),
                address1: addressDoc.get('address1'),
                exterior_number: addressDoc.get('exterior_number'),
                interior_number: addressDoc.get('interior_number'),
                zip: addressDoc.get('zip'),
                state: addressDoc.get('state'),
                county: addressDoc.get('county'),
                neighborhood: addressDoc.get('neighborhood'),
              }
              this._currentAddressItemUid = addressDoc.id;
            }
          }
        });
        return addresses;
      } catch (e) {
        console.log("Error reading addresses: PM01.", e)
        return [];
      }
    }
  }

  async fetchAddressToEdit(userRegistrationUid: string, addressUid: string) {
    try {
      const addressDoc = await getDoc(doc(this.firestore, `userProfile/${userRegistrationUid}/addresses`, addressUid));
      if (addressDoc.exists()) {
        this._currentAddress = {
          active: addressDoc.get('active'),
          position: addressDoc.get('position'),
          address1: addressDoc.get('address1'),
          exterior_number: addressDoc.get('exterior_number'),
          interior_number: addressDoc.get('interior_number'),
          zip: addressDoc.get('zip'),
          state: addressDoc.get('state'),
          county: addressDoc.get('county'),
          neighborhood: addressDoc.get('neighborhood'),
        }

        this._currentAddressItemUid = addressDoc.id;
        return true
      }
    } catch (e) {
      console.log('Error reading address: EA02. ' + e)
    }

    return false;
  }

  async deleteAddress(userRegistrationUid: string, addressUid: string) {
    try {
      await deleteDoc(doc(this.firestore, `userProfile/${userRegistrationUid}/addresses`, addressUid));
      return true;
    } catch (e) {
      console.log('Error deleting address: PM03. ' + e);
    }
    return false;
  }

  async getAddressesCount(userRegistrationId: string) {
    const coll = collection(this.firestore, `userProfile/${userRegistrationId}/addresses`);
    const snapshot = await getCountFromServer(coll);
    return (snapshot.data().count);
  }

  async setActiveItem(userRegistrationId: string, currentAddressItemUid: string) {
    if (await this.authService.getGuestFlag()) {
      let guestId = await this.authService.getGuestUuid();
      let zipCode = await this.getGuestZipCode();
      let position = await this.getGuestPosition();

      this._currentAddress = {
        active: true,
        position: position,
        address1: '',
        exterior_number: '',
        interior_number: '',
        zip: zipCode,
        state: '',
        county: '',
        neighborhood: ''
      };
      this._currentAddressItemUid = `guest_add_${guestId}`;

      return true;
    } else {
      const firestore = this.firestore;
      const docRefPath = `userProfile/${userRegistrationId}/addresses`;

      try {
        const addressesRead = await getDocs(collection(this.firestore, docRefPath))
        addressesRead.forEach(addressDoc => {
          if (addressDoc.exists()) {
            if (currentAddressItemUid == addressDoc.id) {
              updateDoc(doc(firestore, docRefPath, addressDoc.id), { active: true });
              this._currentAddress = {
                active: addressDoc.get('active'),
                position: addressDoc.get('position'),
                address1: addressDoc.get('address1'),
                exterior_number: addressDoc.get('exterior_number'),
                interior_number: addressDoc.get('interior_number'),
                zip: addressDoc.get('zip'),
                state: addressDoc.get('state'),
                county: addressDoc.get('county'),
                neighborhood: addressDoc.get('neighborhood'),
              }
              this._currentAddressItemUid = addressDoc.id;
            } else {
              updateDoc(doc(firestore, docRefPath, addressDoc.id), { active: false });
            }
          }
        });
        return true;

      } catch (e) {
        console.log("Error Setting active address: PM02. ", e)
        return false;
      }
    }

  }

}
